import React from 'react';
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import './style.scss';
import './shared-styles.scss';
import { graphql, Script } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import useWindowSize from '../hooks/useWindowSize';

import MainContainer from '../components/layouts/MainContainer';
import Breadcrumb from '../components/Breadcrumb/Breadcrumb';
import { WPInterlinking } from '../components/WPInterlinking';
import { clickListener } from '../helpers/clickListener.helper';
import {
  getStructuredDataBreadcrumb,
  getStructuredDataOrganization,
  getBreadcrumbsItems,
  getVideoIDs,
  // getVideosStructuredData,
} from '../richSnippets/helpers';

import { getLinkList } from './wpPagesHelpers';
import { WPCommunitiesInterlinking } from '../components/WPCommunitiesInterlinking';

const NewWPPage = ({ pageContext }) => {
  const htmlContent = pageContext.content;
  const cleanHTML = DOMPurify.sanitize(htmlContent, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'target', 'rel'],
  });

  const { localizedPages, seo, translations, i18n } = pageContext;

  const { t } = useTranslation();
  const { width } = useWindowSize();

  React.useEffect(() => {
    const buttonElements = document.querySelectorAll('.button > button');
    for (const button of buttonElements) {
      button.classList.add('trackingCTA');
    }
    window.scrollTo(0, 0);
    window.addEventListener('click', clickListener);
    return () => window.removeEventListener('click', clickListener);
  }, []);

  const { defaultLanguage, language, originalPath } = i18n;
  const { title, metaDesc, breadcrumbs } = seo;

  const homeUri = language === defaultLanguage ? '/' : `/${language}/`;

  const breadcrumbsItems = getBreadcrumbsItems({
    breadcrumbs,
    homePageLabel: t('breadcrumbs.home'),
    homeUri,
  });

  const {
    childrenPages,
    ancestors,
    isSolarParentPage,
    hasInternalLinking,
    conciseBreadcrumbs,
    isCommunitiesPage,
    communityLinks,
    provincesPages,
    hasProvinces,
    hasOrganizationStructuredData,
  } = localizedPages;

  const structuredDataOrganization = hasOrganizationStructuredData
    ? getStructuredDataOrganization({
        description: t('organization.description'),
      })
    : undefined;

  const structuredDataBreadcrumbs =
    getStructuredDataBreadcrumb(breadcrumbsItems);

  const stringToReplace = (string) => {
    return string
      .toLowerCase()
      .search(t('interlinking.stringToReplaceWithIn').toLowerCase()) === -1
      ? string
          .toLowerCase()
          .replace(t('interlinking.stringToReplace').toLowerCase(), '')
      : string
          .toLowerCase()
          .replace(t('interlinking.stringToReplaceWithIn').toLowerCase(), '');
  };

  const adaptedHref = (item) =>
    language === defaultLanguage ? item : item.replace(`/${language}`, '');

  const breadcrumbsUIItems = breadcrumbs.map(
    ({ text: name, url: item }, index) => {
      let label;
      if (index === 0) {
        label = t('breadcrumbs.home');
      } else if (index > 1 && conciseBreadcrumbs) {
        label = stringToReplace(name);
      } else {
        label = name;
      }
      return {
        label,
        href: adaptedHref(item),
      };
    },
  );

  const hasBreadcrumb = breadcrumbs?.length > 2;

  const isProvince =
    ancestors?.nodes?.length === 1 &&
    ancestors?.nodes?.some((node) =>
      node?.acfLocalizedPages?.featureToggles?.some(
        (toggle) => toggle === 'isSolarParentPage',
      ),
    );

  const isMunicipality =
    ancestors?.nodes?.length > 1 &&
    ancestors?.nodes?.some((node) =>
      node?.acfLocalizedPages?.featureToggles?.some(
        (toggle) => toggle === 'isSolarParentPage',
      ),
    );

  const numberOfColumns = width >= 768 ? 4 : 2;

  let linksList = [];

  let nodes;
  switch (true) {
    case isSolarParentPage:
    case hasProvinces: {
      nodes =
        provincesPages?.filter(
          (item) =>
            item?.language?.slug === language &&
            (item?.acfLocalizedPages?.featureToggles === null ||
              item?.acfLocalizedPages?.featureToggles?.find(
                (toggle) => toggle === 'notLocalized',
              ) === undefined),
        ) || [];
      break;
    }
    case isProvince: {
      nodes = childrenPages?.nodes || [];
      break;
    }
    case isMunicipality: {
      const parent = ancestors?.nodes?.filter((node) =>
        node?.acfLocalizedPages?.featureToggles?.every(
          (toggle) => toggle !== 'isSolarParentPage',
        ),
      );
      nodes =
        parent[0]?.wpChildren?.nodes?.filter((municipality) => {
          const adjustedOriginalPath = originalPath.startsWith('/')
            ? originalPath
            : `/${originalPath}`;
          const currentPath =
            language === defaultLanguage
              ? originalPath
              : `/${language}${adjustedOriginalPath}`;

          return (
            municipality?.uri !== currentPath &&
            municipality?.acfLocalizedPages?.featureToggles?.find(
              (toggle) => toggle === 'notLocalized',
            ) === undefined
          );
        }) || [];
      break;
    }
    default: {
      nodes = [];
      break;
    }
  }

  const listOfLinks = nodes.map((item) => ({
    label: stringToReplace(item.title),
    url: item.uri,
  }));

  linksList = getLinkList(listOfLinks, numberOfColumns);

  // const videoIds = getVideoIDs(cleanHTML);
  // const structuredDataYouTubeEmbed = getVideosStructuredData({
  //   videos: pageContext?.videos,
  //   videoIds,
  // });

  return (
    <div className="wp-page">
      <MainContainer
        title={title}
        externalDescription={metaDesc}
        originalPathPage={originalPath}
        hasLanguangeSwitcher
        enableAlternateTags
        translations={translations}
        richSnippets={[
          structuredDataBreadcrumbs,
          structuredDataOrganization,
          // ...structuredDataYouTubeEmbed,
        ]}
      >
        <div className="min-100vh sunhero_container stack">
          {hasBreadcrumb && (
            <Breadcrumb
              list={breadcrumbsUIItems}
              style={{ marginTop: '-1rem', marginBottom: '-1rem' }}
              itemClassName="wpBreadcrumb"
            />
          )}
          {parse(cleanHTML, { trim: true })}
          {linksList.length > 0 &&
            (hasInternalLinking || (!hasInternalLinking && hasProvinces)) && (
              <WPInterlinking
                isParentPage={isSolarParentPage || hasProvinces}
                linksList={linksList}
                language={language}
              />
            )}
          {isCommunitiesPage && (
            <WPCommunitiesInterlinking communityLinks={communityLinks} />
          )}
        </div>
      </MainContainer>
      <Script src="//embed.typeform.com/next/embed.js" />
    </div>
  );
};

export default NewWPPage;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: {
        language: { eq: $language }
        ns: { in: ["interlinking", "footer", "seoNav", "richSnippets"] }
      }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
